/**
 * Device ID Utility — GDPR-Compliant Device Identification
 *
 * Provides privacy-first device identification using random UUIDs.
 *
 * Security Controls:
 * - F1: Device ID generated ONLY after consent (random UUID, NOT fingerprint)
 * - F2: Generic device labels only (no raw user agent strings)
 *
 * GDPR Articles: 5(1)(c) data minimization, 6(1)(a) consent, 25 privacy by design
 */

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Per-device analytics consent record.
 * Stored in user_settings.settings.analytics.devices[deviceId]
 */
export interface AnalyticsConsent {
  enabled: boolean;
  consentDate: string; // ISO 8601 — most recent change
  deviceId: string;
  deviceLabel: string; // F2: Generic label like "Chrome on Windows"
  lastSeen: string; // ISO 8601 — for F5 retention policy
  auditLog: ConsentAuditEntry[]; // F4: Immutable consent history
}

/**
 * Immutable audit log entry for consent changes.
 * GDPR Article 7(1) proof of consent.
 */
export interface ConsentAuditEntry {
  timestamp: string; // ISO 8601 with milliseconds
  action: 'consent_given' | 'consent_withdrawn';
  method:
    | 'banner_accept_button'
    | 'banner_decline_button'
    | 'settings_toggle_on'
    | 'settings_toggle_off'
    | 'migration_from_localstorage';
  consentText?: string; // What user consented to (GDPR Article 7 proof)
}

/**
 * Top-level analytics settings stored in user_settings.settings.analytics
 */
export interface UserAnalyticsSettings {
  version: number; // F6: Optimistic concurrency control
  devices: Record<string, AnalyticsConsent>;
  auditLogArchive?: AuditLogArchiveEntry[]; // C1: Archived audit logs from pruned devices
}

/**
 * Archived audit log entry for pruned devices.
 * Retained for 3 years per GDPR Article 7(1).
 */
export interface AuditLogArchiveEntry {
  deviceId: string;
  deviceLabel: string;
  archivedAt: string; // ISO 8601
  archivedReason: string;
  auditLog: ConsentAuditEntry[];
  consentDate: string;
  lastSeen: string;
}

// ============================================================================
// Constants
// ============================================================================

/** Consent text shown to users in the banner */
export const ANALYTICS_CONSENT_TEXT =
  'We collect anonymous usage data to improve the app during beta testing. ' +
  'This includes feature usage, crash reports, performance data, and generic device info. ' +
  'You can opt out anytime via Settings → Privacy.';

// ============================================================================
// Device ID Functions
// ============================================================================

/**
 * Generate a random device ID (8-16 character hex string).
 *
 * F1: This is a RANDOM UUID, NOT a fingerprint derived from system characteristics.
 * MUST be called ONLY AFTER user consent action (Accept/Decline).
 *
 * @returns Random hex string (8-16 chars)
 */
export function generateDeviceId(): string {
  // Use crypto.randomUUID if available, otherwise fallback
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    // Take first 12 chars of UUID (removes dashes)
    return crypto.randomUUID().replace(/-/g, '').substring(0, 12);
  }

  // Fallback: generate random hex
  const array = new Uint8Array(6);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(array);
  } else {
    // Last resort fallback for environments without crypto
    for (let i = 0; i < array.length; i++) {
      array[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(array)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Get a generic device label from the user agent string.
 *
 * F2: Returns ONLY generic labels like "Chrome on Windows".
 * NO version numbers, build info, or detailed system data.
 *
 * @returns Generic device label matching "Browser on OS" format
 */
export function getDeviceLabel(): string {
  if (typeof navigator === 'undefined' || !navigator.userAgent) {
    return 'Unknown Browser on Unknown OS';
  }

  const ua = navigator.userAgent;
  const browser = detectBrowser(ua);
  const os = detectOS(ua);

  return `${browser} on ${os}`;
}

/**
 * Get existing device ID from sessionStorage, or create a new one.
 *
 * @returns Device ID string
 */
export function getOrCreateDeviceId(): string {
  if (typeof sessionStorage !== 'undefined') {
    const existing = sessionStorage.getItem('device_id');
    if (existing) {
      return existing;
    }
  }

  const newId = generateDeviceId();

  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem('device_id', newId);
  }

  return newId;
}

/**
 * Generate a correlation flow ID for telemetry.
 * M1: 8-char random hex for tracing consent flow across boundaries.
 *
 * @returns 8-character hex string
 */
export function generateFlowId(): string {
  const array = new Uint8Array(4);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(array);
  } else {
    for (let i = 0; i < array.length; i++) {
      array[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(array)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Hash a device ID for telemetry (PII safety).
 * M1: Device IDs MUST be hashed before emission.
 *
 * Uses a simple hash (not cryptographic — sufficient for anonymization in telemetry).
 *
 * @param deviceId - Raw device ID
 * @returns Hashed device ID (hex string)
 */
export function hashDeviceIdForTelemetry(deviceId: string): string {
  // Simple deterministic hash for telemetry anonymization
  let hash = 0;
  for (let i = 0; i < deviceId.length; i++) {
    const char = deviceId.charCodeAt(i);
    hash = ((hash << 5) - hash + char) | 0; // Convert to 32-bit integer
  }
  // Convert to unsigned hex
  return (hash >>> 0).toString(16).padStart(8, '0');
}

// ============================================================================
// Internal Helpers
// ============================================================================

/**
 * Detect browser name from user agent — generic only (F2 compliance).
 * @internal
 */
function detectBrowser(ua: string): string {
  // Order matters — check more specific before generic
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('OPR') || ua.includes('Opera')) return 'Opera';
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  return 'Unknown Browser';
}

/**
 * Detect OS name from user agent — generic only (F2 compliance).
 * @internal
 */
function detectOS(ua: string): string {
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad') || ua.includes('iPod')) return 'iOS';
  if (ua.includes('Mac OS') || ua.includes('Macintosh')) return 'macOS';
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Linux') && !ua.includes('Android')) return 'Linux';
  return 'Unknown OS';
}
